// @ts-nocheck
import React, { FC, useContext, useEffect, useState } from "react";
import { Button, ConfigProvider, Form, Input, Radio, Space, Typography } from "antd";
import { CheckCircleFilled, WarningFilled, WarningOutlined } from "@ant-design/icons";
import { captureException } from "@sentry/react";
import useHttpClient from "../../hooks/useHttpClient";
import {
  filterToDigits,
  getShebaBankName,
  getCardBankName,
  isShebaValid,
  isCardNumberValid,
} from "../../utils/digits";
import { AppContext } from "../../constants/contexts";
import { getErrorMessage } from "../../utils/http";
import BankLogo from "../bankLogo";
import OTPModal from "../OTPModal";

type EditMode = "sheba" | "cardNumber";

const FinancialInfoEdit: FC<{
  data: any;
  onCancel: () => void;
  onSuccess: () => void;
}> = ({ data, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
  const httpClient = useHttpClient();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState<EditMode>("cardNumber");
  const { messageApi } = useContext(AppContext);

  // Watch form field values to update addonAfter reactively
  const shebaValue = Form.useWatch("sheba", form);
  const cardNumberValue = Form.useWatch("cardNumber", form);

  useEffect(() => {
    // Set initial values when component mounts or data changes
    const bankAccount = data?.bankAccount || {};
    form.setFieldsValue({
      sheba: bankAccount.sheba || "",
      cardNumber: bankAccount.cardNumber || "",
    });
    setEditMode("cardNumber");
  }, [data, form]);

  function getShebaPostFix() {
    if (!shebaValue || !isShebaValid(shebaValue)) {
      return <WarningFilled />;
    }
    const name = getShebaBankName(shebaValue);
    if (!name) {
      return <CheckCircleFilled />;
    }
    return (
      <div className="flex items-center gap-1 h-full">
        <CheckCircleFilled />
        <BankLogo name={name} size="sm" />
        {name}
      </div>
    );
  }

  function getCardPostFix() {
    if (!cardNumberValue) {
      return <WarningFilled />;
    }
    const name = getCardBankName(cardNumberValue, false);
    if (!name) {
      return <WarningOutlined />;
    }
    return (
      <div className="flex items-center gap-1 h-full">
        {name}
        <BankLogo name={name} size="sm" />
      </div>
    );
  }

  function serializeForm() {
    const values = form.getFieldsValue();
    const bankAccount: any = {};

    if (editMode === "sheba") {
      bankAccount.sheba = filterToDigits(values.sheba);
      bankAccount.cardNumber = filterToDigits(values.cardNumber);
    } else {
      bankAccount.cardNumber = filterToDigits(values.cardNumber);
    }

    return { ...bankAccount };
  }

  async function handleSubmitWithOTP(OTP: string) {
    try {
      setLoading(true);
      await httpClient.put("/users/me/financial", serializeForm(), {
        headers: { OTP },
      });
    } catch (error) {
      captureException(error);
    }
  }

  async function handleSubmit() {
    setLoading(true);
    setIsOTPModalOpen(true);
  }

  return (
    <div className="mt-4">
      <Form form={form} layout="vertical"
              variant='filled'

        onFinish={handleSubmit} autoComplete="off">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Radio.Group
              value={editMode}
              onChange={(e) => {
                setEditMode(e.target.value);
                // Clear validation errors when switching modes
                form.setFields([
                  { name: "sheba", errors: [] },
                  { name: "cardNumber", errors: [] },
                ]);
              }}
              style={{ width: "100%" }}
            >
              <Space direction="vertical">
                <Radio value="sheba">ویرایش شماره شبا</Radio>
                <Radio value="cardNumber">ویرایش شماره کارت</Radio>
              </Space>
            </Radio.Group>
            <Typography.Text type="secondary" className="text-sm block mt-2">
              {editMode === "sheba"
                ? "لطفا شماره شبا و شماره کارت را وارد کنید"
                : "شماره شبا به صورت خودکار از روی شماره کارت دریافت می‌شود"}
            </Typography.Text>
          </div>

          <ConfigProvider direction="ltr">
            {editMode === "sheba" ? (
              <>
                <Form.Item
                  dependencies={["sheba"]}
                  label="شماره شبا"
                  name="sheba"
                  className="h-[40px]"
                  rules={[
                    { required: true, message: "این فیلد اجباری است" },
                    {
                      message: "شماره شبا معتبر نیست",
                      validator: (_, value) =>
                        value && !isShebaValid(value) ? Promise.reject() : Promise.resolve(),
                    },
                    // {
                    //   message: "متاسفانه در حال‌حاضر حساب بلوبانک پشتیبانی نمیشود!",
                    //   validator: (_, value) =>
                    //     value && getShebaBankName(value) === "بلو"
                    //       ? Promise.reject()
                    //       : Promise.resolve(),
                    // },
                  ]}
                >
                  <Input
                    addonBefore="IR"
                    addonAfter={getShebaPostFix()}
                    styles={{ input: { height: 40 } }}
                    className="h-[40px] text-left text-base"
                    style={{ direction: "ltr", textAlign: "left", height: 40 }}
                    placeholder=""
                  />
                </Form.Item>
                <Form.Item
                  className="mt-14"
                  dependencies={["cardNumber"]}
                  label="شماره کارت"
                  name="cardNumber"
                  rules={[
                    { required: true, message: "این فیلد اجباری است" },
                    {
                      message: "شماره کارت معتبر نیست",
                      validator: (_, value) =>
                        value && !isCardNumberValid(value) ? Promise.reject() : Promise.resolve(),
                    },
                  ]}
                >
                  <Input
                    styles={{ input: { height: 40 } }}
                    addonAfter={getCardPostFix()}
                    style={{ direction: "ltr" }}
                    placeholder="16 رقم شماره کارت"
                  />
                </Form.Item>
              </>
            ) : (
              <Form.Item
                dependencies={["cardNumber"]}
                label="شماره کارت"
                name="cardNumber"
                rules={[
                  { required: true, message: "این فیلد اجباری است" },
                  {
                    message: "شماره کارت معتبر نیست",
                    validator: (_, value) =>
                      value && !isCardNumberValid(value) ? Promise.reject() : Promise.resolve(),
                  },
                ]}
              >
                <Input
                  styles={{ input: { height: 40 } }}
                  addonAfter={getCardPostFix()}
                  style={{ direction: "ltr" }}
                  placeholder="16 رقم شماره کارت"
                />
              </Form.Item>
            )}
          </ConfigProvider>

          <Form.Item className="mb-0">
            <Space>
              <Button onClick={onCancel}>انصراف</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                ذخیره
              </Button>
            </Space>
          </Form.Item>
        </Space>
      </Form>
      <OTPModal
        type="FINANCIAL_INFO_UPDATE"
        url="/users/me/financial"
        method="put"
        payload={serializeForm()}
        requestOtp
        open={isOTPModalOpen}
        onClose={() => {
          setIsOTPModalOpen(false);
          setLoading(false);
        }}
        onSuccess={() => {
          setIsOTPModalOpen(false);
          onSuccess();
          onCancel();
          form.resetFields();
        }}
      />
    </div>
  );
};

export default FinancialInfoEdit;
